<template>
  <div class="cosmo-claw-chat">

    <!-- 数字人信息 + 头部按钮: overlay 模式 (抽屉) -->
    <template v-if="enlargeable">
      <div class="chat-header">
      <div
        :class="['claw-digital-info', { 'is-placeholder': !(digitalPersonInfo && messages.length > 0 && !isAgent) }]"
      >
        <img v-if="digitalPersonInfo && digitalPersonInfo.logo" :src="digitalPersonInfo.logo" class="digital-avatar" />
        <div class="digital-text">
          <div class="digital-title-row">
            <div class="digital-title">{{ digitalPersonInfo ? digitalPersonInfo.title : '' }}</div>
            <div v-if="showSupplyChainSettings" class="supply-settings-btn" @click="handleSupplyChainSettings">
              <i class="el-icon-set-up"></i>
            </div>
          </div>
          <div class="digital-subtitle">{{ digitalPersonInfo ? digitalPersonInfo.subtitle : '' }}</div>
        </div>
      </div>

      <div class="claw-mode-toggle">
        <el-tooltip content="新会话" placement="bottom">
          <div class="zoom-btn" @click="handleNewSession">
            <LineIcon name="newChat" class="zoom-btn__icon" />
          </div>
        </el-tooltip>
        <el-tooltip :content="$t('workbench.toolCallToggle')" placement="bottom">
          <div class="zoom-btn" :class="{ active: showTools }" @click="toggleTools">
            <LineIcon name="tool" class="zoom-btn__icon" />
          </div>
        </el-tooltip>
        <el-tooltip v-if="!isAgent && showZoomBtn" :content="enlargeable ? (enlarged ? $t('workbench.zoomOut') : $t('workbench.zoomIn')) : (mode === 'assistant' ? $t('workbench.zoomIn') : $t('workbench.zoomOut'))" placement="bottom">
          <div class="zoom-btn" :class="{ active: enlargeable && enlarged }" @click="handleZoomToggle">
            <LineIcon :name="zoomIconName" class="zoom-btn__icon" />
          </div>
        </el-tooltip>
        <el-tooltip v-if="hasHistorySession" content="过程文件" placement="bottom">
          <div class="zoom-btn" :class="{ active: showFilePanel }" @click="toggleFilePanel">
            <LineIcon name="folder" class="zoom-btn__icon" />
          </div>
        </el-tooltip>
      </div>
      </div>
    </template>

    <div class="chat-body" :class="{ 'with-panel': showFilePanel && !enlargeable }">
      <div class="chat-main" :class="{ 'push-mode': !enlargeable }" v-show="chatContentVisible">
        <!-- 数字人信息 + 头部按钮: push 模式 (独立页面) -->
        <template v-if="!enlargeable">
          <div class="chat-header push" :class="{ 'is-session-page': showSwitcher }">
          <div v-if="showSwitcher" class="claw-digital-info push session-switcher-wrap">
            <DigitalHumanSwitcher />
            <div v-if="showSupplyChainSettings" class="supply-settings-btn" @click="handleSupplyChainSettings">
              <i class="el-icon-set-up"></i>
            </div>
          </div>
          <div
            v-else
            :class="['claw-digital-info', 'push', { 'is-placeholder': !(digitalPersonInfo && messages.length > 0 && !isAgent) }]"
          >
            <img v-if="digitalPersonInfo && digitalPersonInfo.logo" :src="digitalPersonInfo.logo" class="digital-avatar" />
            <div class="digital-text">
              <div class="digital-title-row">
                <div class="digital-title">{{ digitalPersonInfo ? digitalPersonInfo.title : '' }}</div>
                <div v-if="showSupplyChainSettings" class="supply-settings-btn" @click="handleSupplyChainSettings">
                  <i class="el-icon-set-up"></i>
                </div>
              </div>
              <div class="digital-subtitle">{{ digitalPersonInfo ? digitalPersonInfo.subtitle : '' }}</div>
            </div>
          </div>

          <div class="claw-mode-toggle push" :class="{ 'is-flat': showSwitcher }">
            <el-tooltip content="新会话" placement="bottom">
              <div class="zoom-btn" @click="handleNewSession">
                <LineIcon name="newChat" class="zoom-btn__icon" />
              </div>
            </el-tooltip>
            <el-tooltip :content="$t('workbench.toolCallToggle')" placement="bottom">
              <div class="zoom-btn" :class="{ active: showTools }" @click="toggleTools">
                <LineIcon name="tool" class="zoom-btn__icon" />
              </div>
            </el-tooltip>
            <el-tooltip v-if="hasHistorySession" content="过程文件" placement="bottom">
              <div class="zoom-btn" :class="{ active: showFilePanel }" @click="toggleFilePanel">
                <LineIcon name="folder" class="zoom-btn__icon" />
              </div>
            </el-tooltip>
          </div>
          </div>
        </template>

        <CosmoClawMessageList
          ref="cosmoClawMessageList"
          :wide-tool-card="wideToolCard"
          :show-rail="showRail"
          :rail-nodes="turns"
          :rail-active-index="railActiveIndex"
          :rail-mounted-keys="railMountedKeys"
          :search-keyword="searchKeyword"
          @rail-select="railOnSelect"
        />
        <CosmoClawInput ref="cosmoClawInput" preserve-draft />
      </div>
      <ProcessFilePanel
        v-if="showFilePanel"
        :mode="panelMode"
        :maximized.sync="filePanelMaximized"
        :conversation-id="currentSessionKey"
        @close="handleClosePanel"
      />
    </div>

    <CosmoClawDrawer />
    <QuotePreviewDrawerHost />
    <FuzzyQuotePreviewDrawerHost />

    <TaskProgressPanel />
    <!-- 追问悬浮 island（选中正文 → 追问 / 编辑追问） -->
    <FollowUpIsland />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

const CEO_INFO = {
  titleKey: 'workbench.digitalCEOAssistant',
  subtitleKey: 'workbench.digitalCEOAssistantDesc',
  logo: require('@/assets/imgs/workbench/ceo.png')
}
const GC_CEO_INFO = {
  titleKey: 'workbench.digitalCEOAssistant',
  subtitleKey: 'workbench.digitalCEOAssistantDesc',
  logo: require('@/assets/imgs/workbench/ceo.png')
}
const CEO_DEMO_INFO = {
  titleKey: 'workbench.digitalCEOAssistant',
  subtitleKey: 'workbench.digitalCEOAssistantDesc',
  logo: require('@/assets/imgs/workbench/ceo.png')
}
const CFO_INFO = {
  titleKey: 'workbench.digitalCFOAssistant',
  subtitleKey: 'workbench.digitalCFOAssistantDesc',
  logo: require('@/assets/imgs/workbench/cfoLogo.png')
}
const CSO_INFO = {
  titleKey: 'workbench.digitalSupplyChainAssistant',
  subtitleKey: 'workbench.digitalSupplyChainAssistantDesc',
  logo: require('@/assets/imgs/workbench/csoLogo.png')
}
const DIRECTOR_INFO = {
  titleKey: 'workbench.digitalFactoryAssistant',
  subtitleKey: 'workbench.digitalFactoryAssistantDesc',
  logo: require('@/assets/imgs/workbench/gongzhang.png')
}
const DIGITAL_PERSON_MAP = {
  cai_wu_shu_zi_ren: CFO_INFO,
  gong_ying_lian_shu_zi_ren: CSO_INFO,
  chang_zhang_shu_zi_ren: DIRECTOR_INFO
}

import CosmoClawMessageList from './CosmoClawMessageList.vue'
import CosmoClawInput from './CosmoClawInput.vue'
import CosmoClawDrawer from './CosmoClawDrawer.vue'
import TaskProgressPanel from './md-components/TaskProgressPanel.vue'
import ProcessFilePanel from './ProcessFilePanel.vue'
import LineIcon from './LineIcon.vue'
import DigitalHumanSwitcher from '@/views/smart-engine-new/components/DigitalHumanSwitcher.vue'
import QuotePreviewDrawerHost from '@/views/workbench-new/sale/QuotePreviewDrawerHost.vue'
import FuzzyQuotePreviewDrawerHost from '@/views/workbench-new/sale/FuzzyQuotePreviewDrawerHost.vue'
import FollowUpIsland from './FollowUpIsland.vue'
import { followUpStore } from '../followUp/store'
import { getSupplyChainAvatar, SUPPLY_CHAIN_INTERNAL_NAME } from '../utils/digitalHumanAvatar'
import { buildTurns } from '../utils/turnBuilding'

export default {
  name: 'CosmoClawChat',
  components: { CosmoClawMessageList, CosmoClawInput, CosmoClawDrawer, TaskProgressPanel, ProcessFilePanel, DigitalHumanSwitcher, QuotePreviewDrawerHost, FuzzyQuotePreviewDrawerHost, FollowUpIsland, LineIcon },
  props: {
    mode: {
      type: String,
      default: 'assistant'
    },
    isAgent: {
      type: Boolean,
      default: false
    },
    // 抽屉场景：放大按钮改为控制抽屉宽度（enlarged 由父组件持有）
    enlargeable: {
      type: Boolean,
      default: false
    },
    enlarged: {
      type: Boolean,
      default: false
    },
    // 是否显示放大按钮：仅抽屉内的 cosmo-claw-chat 显示，独立会话页隐藏（默认 true 兼容旧用法）
    showZoomBtn: {
      type: Boolean,
      default: true
    },
    // 是否让工具调用卡片以宽屏形态(半宽)展示：由入口显式声明(抽屉放大态 / 数字人会话页),默认 false 不影响其它入口
    wideToolCard: {
      type: Boolean,
      default: false
    },
    // 数字人会话页头部：左侧展示数字人切换器（默认 false，PC 智能工作台等入口保持原样）
    showSwitcher: {
      type: Boolean,
      default: false
    },
    // 会话搜索关键词：从搜索结果进入会话时用于高亮消息正文
    searchKeyword: {
      type: String,
      default: ''
    }
  },
  computed: {
    ...mapState('cosmoClaw', ['socketState', 'isHandshaked', 'showTools', 'sessions', 'currentSessionKey', 'messages', 'isCeoDemo', 'isGc', 'isLqz']),
    ...mapGetters(['currentWorkbench']),
    profileAvatar() {
      // 仅供应链数字人使用数字人档案头像，不影响 CEO/财务等其它数字人
      if (this.$store.state.cosmoClaw.currentAgentName !== SUPPLY_CHAIN_INTERNAL_NAME) return ''
      return getSupplyChainAvatar(this.$store.state.user.agentList)
    },
    digitalPersonInfo() {
      const agentName = this.$store.state.cosmoClaw.currentAgentName
      if (!agentName) return null
      let info = DIGITAL_PERSON_MAP[agentName]
      if (!info && agentName === 'ceo_shu_zi_ren') {
        if (this.isGc || this.isLqz) info = GC_CEO_INFO
        else if (this.isCeoDemo) info = CEO_DEMO_INFO
        else info = CEO_INFO
      }
      if (!DIGITAL_PERSON_MAP[agentName] && this.currentWorkbench && this.currentWorkbench.name && this.currentWorkbench.name.includes('厂长')) {
        info = DIRECTOR_INFO
      }
      if (!info) return null
      return {
        title: this.$t(info.titleKey),
        subtitle: this.$t(info.subtitleKey),
        logo: this.profileAvatar || info.logo
      }
    },
    showSupplyChainSettings() {
      if (this.$store.state.cosmoClaw.currentAgentName !== 'gong_ying_lian_shu_zi_ren') return false
      const userCode = this.$store.state.user.userInfo?.userCode
      if (!userCode) return false
      const isProd = process.env.VUE_APP_ENV === 'prod'
      const allowedCode = isProd ? 'czwl@cosmoplat.cn' : '01506650'
      return userCode === allowedCode
    },
    panelMode() {
      return this.enlargeable ? 'overlay' : 'push'
    },
    // 放大按钮图标：抽屉内按 enlarged 切换，独立页按 mode 切换
    zoomIconName() {
      if (this.enlargeable) return this.enlarged ? 'shrink' : 'expand'
      return this.mode === 'assistant' ? 'expand' : 'shrink'
    },
    chatContentVisible() {
      if (!this.showFilePanel) return true
      if (this.enlargeable) return false
      return !this.filePanelMaximized
    },
    hasHistorySession() {
      const key = this.currentSessionKey
      if (!key) return false
      const session = this.sessions.find(
        (s) => s.key === key || s.globalKey === key
      )
      return !!session && !session.isPending
    },
    // 过滤后的消息列表（与 CosmoClawMessageList.visibleMessages 保持一致）
    filteredMessages() {
      const sk = this.currentSessionKey
      if (!sk) return this.messages
      return this.messages.filter((m) => !m._sessionKey || m._sessionKey === sk)
    },
    // 归并后的 turn 序列：给刻度尺 nodes 用（基于过滤后的消息，确保与 DOM 锚点一致）
    turns() {
      return buildTurns(this.filteredMessages)
    },
    // 是否显示刻度尺：turn 数量 > 1 才够导航意义
    showRail() {
      return this.turns && this.turns.length > 1
    }
  },
  data() {
    return {
      showFilePanel: false,
      filePanelMaximized: false,
      // 消息导航刻度尺（方案 B）
      railActiveIndex: null,
      railMountedKeys: {},
      railObserver: null,
      railScrollContainer: null,
      railRetryTimer: null
    }
  },
  watch: {
    currentSessionKey() {
      this.showFilePanel = false
      this.filePanelMaximized = false
      // 追问注解只属于当前会话，切换会话时清空
      followUpStore.reset()
    },
    // 消息列表变化（新增/切换会话）→ 重新初始化 observer 锚点
    messages: {
      deep: false,
      handler() {
        // 延迟两帧（nextTick + rAF）再初始化：
        //   一帧等待 Vue DOM 更新 + 兄弟组件 pinToBottom 执行；
        //   第二帧等待 scrollTop 被正确设置后再算 activeIndex，避免
        //   "scrollTop=0 时误判为中间位置" 的 race condition。
        this.$nextTick(() => {
          this.railMountedKeys = {}
          this._initRailObserver()
          // 再延迟一帧重算一次，覆盖 autoScroll pinToBottom + scroll 事件后最稳定的几何
          requestAnimationFrame(() => {
            this._onRailScroll()
          })
          // 兜底：再延迟 100ms 重算（覆盖图片/富文本异步渲染完导致的 scrollHeight 变化）
          setTimeout(() => this._onRailScroll(), 100)
        })
      }
    }
  },
  mounted() {
    document.addEventListener('md-card-action', this._handleCardAction)
    // 同样延迟一帧，等初始 DOM 渲染完再初始化
    this.$nextTick(() => this._initRailObserver())
    requestAnimationFrame(() => this._onRailScroll())
    setTimeout(() => this._onRailScroll(), 100)
  },
  beforeDestroy() {
    this.cancelRecording()
    document.removeEventListener('md-card-action', this._handleCardAction)
    clearTimeout(this.railRetryTimer)
    if (this.railObserver) {
      this.railObserver.disconnect()
      this.railObserver = null
    }
    if (this.railScrollContainer) {
      this.railScrollContainer.removeEventListener('scroll', this._onRailScroll)
      this.railScrollContainer = null
    }
  },
  methods: {
    handleZoomToggle() {
      // 抽屉放大按钮：只控制抽屉宽度；非抽屉场景保持原模式切换
      if (this.enlargeable) {
        this.$emit('toggle-enlarge')
        return
      }
      this.switchMode(this.mode === 'assistant' ? 'digital' : 'assistant')
    },
    switchMode(mode) {
      this.$emit('mode-change', mode)
    },
    cancelRecording() {
      const input = this.$refs.cosmoClawInput
      if (input && typeof input.cancelRecording === 'function') {
        input.cancelRecording()
      }
    },
    _handleCardAction(e) {
      // 禁止不可见实例处理卡片事件（避免 drawer + 侧栏双实例重复发送）
      if (!this.$el || !this.$el.offsetParent) return
      const { action, message } = e.detail || {}
      if (action === 'pvmc-gross-margin-analysis') return
      if (message) {
        // 同步终结上一轮流式 assistant，避免在 dispatch sendMessage 之前
        // WebSocket 的 'assistant' 事件覆盖旧消息内容（移除 loading 卡片 JSON）
        this.$store.commit('cosmoClaw/FINALIZE_PREVIOUS_STREAMING')
        this.$store.dispatch('cosmoClaw/sendMessage', { content: message })
      }
    },
    toggleTools() {
      this.$store.commit('cosmoClaw/SET_SHOW_TOOLS', !this.showTools)
    },
    toggleFilePanel() {
      this.showFilePanel = !this.showFilePanel
      if (!this.showFilePanel) {
        this.filePanelMaximized = false
      }
    },
    handleClosePanel() {
      this.showFilePanel = false
      this.filePanelMaximized = false
    },
    handleSupplyChainSettings() {
      document.dispatchEvent(new CustomEvent('cosmo-claw-open-preference'))
    },
    refreshData() {
      this.$store.dispatch('cosmoClaw/refreshData')
    },
    handleNewSession() {
      this.$store.dispatch('cosmoClaw/createNewSession')
      const input = this.$refs.cosmoClawInput
      if (input) {
        if (typeof input.clearInput === 'function') {
          input.clearInput()
        } else if (typeof input.cancelRecording === 'function') {
          input.cancelRecording()
        }
      }
      this.$emit('new-session')
    },

    /* ============ 消息导航刻度尺 ============ */

    // 找滚动容器 + 初始化 observer 和 scroll 监听
    // 历史记录 / 切换会话时 DOM 可能延迟渲染 → 自动重试几次
    _initRailObserver(retry = 0) {
      // 滚动容器：CosmoClawMessageList 的 ref="container"
      const listRef = this.$refs.cosmoClawMessageList
      const scrollContainer = listRef && listRef.$refs && listRef.$refs.container
        ? listRef.$refs.container
        : null

      // 容器还没 ready → 延迟重试（最多 5 次，每次间隔 150ms）
      if (!scrollContainer) {
        if (retry < 5) {
          clearTimeout(this.railRetryTimer)
          this.railRetryTimer = setTimeout(() => this._initRailObserver(retry + 1), 150)
        }
        return
      }

      // 只有容器引用真的变了才重建 scroll 监听 + IO（避免 messages 频繁变化时不必要的 remove/add）
      const containerChanged = this.railScrollContainer !== scrollContainer
      this.railScrollContainer = scrollContainer

      if (containerChanged) {
        if (this.railObserver) {
          this.railObserver.disconnect()
          this.railObserver = null
        }
        scrollContainer.removeEventListener('scroll', this._onRailScroll)
        scrollContainer.addEventListener('scroll', this._onRailScroll, { passive: true })
      }

      // 立即算一次 activeIndex
      this._onRailScroll()

      // 用 IntersectionObserver 标记哪些 turn 进过视口（mountedKeys）
      const anchorNodes = scrollContainer.querySelectorAll('[data-turn-key]')
      if (anchorNodes.length === 0) {
        // 消息 DOM 还没渲染出来 → 延迟重试
        if (retry < 5) {
          clearTimeout(this.railRetryTimer)
          this.railRetryTimer = setTimeout(() => this._initRailObserver(retry + 1), 150)
        }
        return
      }

      // 每次都重建 IO（anchorNodes 数量会变）
      if (this.railObserver) {
        this.railObserver.disconnect()
        this.railObserver = null
      }
      const io = new IntersectionObserver((entries) => {
        const newKeys = { ...this.railMountedKeys }
        let changed = false
        for (const e of entries) {
          const key = e.target.getAttribute('data-turn-key')
          if (!key) continue
          if (e.isIntersecting && !newKeys[key]) {
            newKeys[key] = true
            changed = true
          }
        }
        if (changed) this.railMountedKeys = newKeys
      }, {
        root: scrollContainer,
        rootMargin: '0px 0px -60% 0px', // 进入视口下方 40% 就标记 mounted
        threshold: 0.1
      })
      for (const n of anchorNodes) io.observe(n)
      this.railObserver = io
    },

    // 滚动时算 activeIndex —— 客户端 ChatDisplay.tsx L1792-L1837 移植
    _onRailScroll() {
      if (!this.railScrollContainer) return
      const container = this.railScrollContainer
      const sc = container.scrollHeight - container.scrollTop - container.clientHeight < 40
      const anchors = container.querySelectorAll('[data-turn-key]')
      if (!anchors.length) { this.railActiveIndex = null; return }

      let active = anchors.length - 1 // 默认最后一个
      if (!sc) {
        // 非底部：取"顶边已到达视口顶部"的最后一个锚点（+1px 容差）。
        // 刻度尺点击后目标气泡被顶部对齐，其 absTop 正好等于 scrollTop，
        // 沿用旧的 40% 判定会把高亮错标成下一条。
        const viewportTop = container.scrollTop + 1
        const containerRect = container.getBoundingClientRect()
        active = 0
        for (let i = 0; i < anchors.length; i++) {
          const rect = anchors[i].getBoundingClientRect()
          const absTop = rect.top - containerRect.top + container.scrollTop
          if (absTop > viewportTop) break
          active = i
        }
      }

      // 把 DOM 索引映射回 turn index（锚点顺序和 turns 顺序一致）
      const nodes = this.turns || []
      const keyOf = (i) => anchors[i] && anchors[i].getAttribute('data-turn-key')
      for (let j = 0; j < nodes.length; j++) {
        if (nodes[j].key === keyOf(active)) {
          this.railActiveIndex = j
          return
        }
      }
      this.railActiveIndex = active < nodes.length ? active : nodes.length - 1
    },

    // 刻度尺点击 → 把对应 user 气泡定位到会话滚动区顶部
    railOnSelect(index) {
      const turn = this.turns && this.turns[index]
      if (!turn) return
      const container = this.railScrollContainer
      if (!container) return
      const anchor = container.querySelector(`[data-turn-key="${turn.key}"]`)
      if (!anchor) return
      // 解除 rAF 自动贴底锁定：否则 pinToBottom 会在下一帧把滚动拉回底部，表现为"点击无效"
      const listRef = this.$refs.cosmoClawMessageList
      if (listRef && typeof listRef.pauseAutoScroll === 'function') {
        listRef.pauseAutoScroll()
      }
      // 顶部对齐：block:'center' 会把气泡滚到视口中部，与"定位到会话页面头部"不符
      const containerRect = container.getBoundingClientRect()
      const anchorRect = anchor.getBoundingClientRect()
      const targetTop = container.scrollTop + anchorRect.top - containerRect.top
      const maxTop = Math.max(container.scrollHeight - container.clientHeight, 0)
      const top = Math.min(Math.max(targetTop, 0), maxTop)
      if (typeof container.scrollTo === 'function') {
        container.scrollTo({ top, behavior: 'smooth' })
      } else {
        container.scrollTop = top
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.cosmo-claw-chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  // 聊天区域背景透明：不影响数字人头像/描述(.claw-digital-info)和按钮行(.claw-mode-toggle)
  // 它们有独立样式，背景透明后视觉上与外层容器(抽屉/页面)融为一体，参考 COSMO-Sphere Client
  background: transparent;
  position: relative;
  border-radius: 8px;
}

.chat-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;
  overflow: hidden;

  &.with-panel {
    flex-direction: row;
  }
}

.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  position: relative; // 给 rail absolute 定位做参考

  &.push-mode {
    position: relative;
    // padding-top: 54px;
    padding-top: 4px;
  }
}

/* ---- rail 已内嵌到 CosmoClawMessageList 内部，样式在那里定义 ---- */

/* 统一头部容器:横跨顶部 + 磨砂背景 + flex 布局(左头像+名称,右按钮) */
.chat-header {
  // position: absolute;
  // top: 0;
  // left: 0;
  // right: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  // 参考目标项目磨砂背景效果(glassmorphism):半透明白色 + backdrop-filter 模糊
  // 与下方滚动的聊天内容浑然一体,消息滚动时能透出模糊内容
  // background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);

  // &.push {
  //   position: absolute;
  //   top: 0;
  //   left: 0;
  //   right: 0;
  // }

  /* 数字人会话页头部：按 UI 稿还原（左侧切换器 + 右侧扁平图标，无磨砂与分隔线） */
  &.is-session-page {
    padding: 10px 20px;
    border-bottom: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
}

/* 会话页头部左侧：切换器 + 供应链设置入口 */
.session-switcher-wrap {
  gap: 4px;
}

.claw-mode-toggle {
  display: flex;
  gap: 4px;
}

/* 数字人信息头部 */
.claw-digital-info {
  display: flex;
  align-items: center;
  gap: 10px;

  /* 无数字人信息时占位隐藏(visibility 保留布局空间,避免右侧按钮被 space-between 移到左侧) */
  &.is-placeholder {
    visibility: hidden;
  }

  .digital-avatar {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
  }
  .digital-text {
    display: flex;
    flex-direction: column;
    .digital-title-row {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .digital-title {
      font-size: 14px;
      font-weight: 600;
      color: #1f2329;
      line-height: 1.3;
    }
    .digital-subtitle {
      font-size: 11px;
      color: #8f959e;
      line-height: 1.3;
      margin-top: 1px;
    }
    .supply-settings-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      border-radius: 4px;
      color: #c0c4cc;
      font-size: 14px;
      cursor: pointer;
      transition: color 0.3s ease;
      flex-shrink: 0;

      &:hover {
        color: #4e5969;
      }
    }
  }
}

.zoom-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: white;
  color: #4e5969;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;

  .zoom-btn__icon {
    width: 16px;
    height: 16px;
  }

  /* 选中态与 hover：主题蓝底 + 白色图标 */
  &.active {
    background: #4a5ff3;
    color: #fff;
    box-shadow: 0 2px 6px rgba(74, 95, 243, 0.35);
  }

  &:hover {
    background: #4a5ff3;
    color: #fff;
    box-shadow: 0 2px 6px rgba(74, 95, 243, 0.35);
  }
}

/* 会话页头部右侧按钮：按 UI 稿使用无底色的扁平图标 */
.claw-mode-toggle.is-flat {
  gap: 18px;
  align-items: center;

  .zoom-btn {
    width: 24px;
    height: 24px;
    border-radius: 4px;
    background: white;
    color: #1f2329;
    // font-size: 17px;
    box-shadow: none;

    /* hover 与选中态：主题蓝底 + 白色图标（SVG 走 currentColor，无需额外反白） */
    &.active,
    &:hover {
      background: #4a5ff3;
      color: #fff;
      box-shadow: none;
    }
  }
}
</style>
